import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  storeApi,
  type CreateStorePayload,
  type UpdateStorePayload,
  type CreateStoreServicePayload,
  type UpdateStoreServicePayload,
  type CreateStoreRecordPayload,
  type UpdateStoreRecordPayload,
  type CreateStoreExpensePayload,
  type UpdateStoreExpensePayload,
  type CreateStoreIncomePayload,
  type UpdateStoreIncomePayload,
  type SaveStoreConfigPayload,
} from "../lib/storeApi";

export const STORE_KEYS = {
  all: ["store"] as const,
  stores: (businessId?: string | null) => [...STORE_KEYS.all, "stores", businessId ?? "all"] as const,
  services: (storeId: string) => [...STORE_KEYS.all, "services", storeId] as const,
  records: (storeId: string, prefix: string, staffId?: string) =>
    [...STORE_KEYS.all, "records", storeId, prefix, staffId ?? "all"] as const,
  recordsInfinite: (storeId: string, prefix: string, createdBy?: string, serviceId?: string) =>
    [...STORE_KEYS.all, "records-infinite", storeId, prefix, createdBy ?? "all", serviceId ?? "all"] as const,
  staff: (storeId: string) => [...STORE_KEYS.all, "staff", storeId] as const,
  config: (storeId: string) => [...STORE_KEYS.all, "config", storeId] as const,
};

export function useStores(businessId?: string | null) {
  return useQuery({
    queryKey: STORE_KEYS.stores(businessId),
    queryFn: () => storeApi.listStores(businessId).then((r) => r.items),
    enabled: businessId !== undefined,
  });
}

export function useCreateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStorePayload) => storeApi.createStore(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: STORE_KEYS.all }),
  });
}

export function useUpdateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ storeId, payload }: { storeId: string; payload: UpdateStorePayload }) =>
      storeApi.updateStore(storeId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: STORE_KEYS.all }),
  });
}

export function useStoreServices(storeId: string) {
  return useQuery({
    queryKey: STORE_KEYS.services(storeId),
    queryFn: () => storeApi.listServices(storeId).then((r) => r.items),
    enabled: !!storeId,
  });
}

export function useCreateStoreService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStoreServicePayload) => storeApi.createService(payload),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: STORE_KEYS.services(vars.storeId) }),
  });
}

export function useUpdateStoreService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceId, payload }: { serviceId: string; payload: UpdateStoreServicePayload }) =>
      storeApi.updateService(serviceId, payload),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: STORE_KEYS.services(vars.payload.storeId) }),
  });
}

export function useDeleteStoreService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceId, storeId }: { serviceId: string; storeId: string }) =>
      storeApi.deleteService(serviceId, storeId),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: STORE_KEYS.services(vars.storeId) }),
  });
}

const RECORDS_PAGE_SIZE = 30;

export function useStoreRecords(storeId: string, prefix: string, staffId?: string) {
  return useQuery({
    queryKey: STORE_KEYS.records(storeId, prefix, staffId),
    queryFn: () => storeApi.listRecords(storeId, prefix, { staffId }).then((r) => r.items),
    enabled: !!storeId && !!prefix,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useStoreRecordsInfinite(
  storeId: string,
  prefix: string,
  createdBy?: string,
  serviceId?: string,
) {
  return useInfiniteQuery({
    queryKey: STORE_KEYS.recordsInfinite(storeId, prefix, createdBy, serviceId),
    queryFn: ({ pageParam }) =>
      storeApi.listRecords(storeId, prefix, {
        createdBy,
        serviceId,
        limit: RECORDS_PAGE_SIZE,
        lastKey: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextKey ?? undefined,
    enabled: !!storeId && !!prefix,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useStoreStaff(storeId: string) {
  return useQuery({
    queryKey: STORE_KEYS.staff(storeId),
    queryFn: () => storeApi.listStaff(storeId).then((r) => r.items),
    enabled: !!storeId,
    staleTime: 0,
  });
}

export function useStaffWalletBalances(storeId: string | null | undefined) {
  return useQuery({
    queryKey: [...STORE_KEYS.all, "wallet-balances", storeId ?? ""] as const,
    queryFn: () => storeApi.listStaffWalletBalances(storeId!).then((r) => r.items),
    enabled: !!storeId,
    staleTime: 0,
  });
}

export function useCreateStoreRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStoreRecordPayload) => storeApi.createRecord(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: STORE_KEYS.all }),
  });
}

export function useUpdateStoreRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, payload }: { recordId: string; payload: UpdateStoreRecordPayload }) =>
      storeApi.updateRecord(recordId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: STORE_KEYS.all }),
  });
}

export function useDeleteStoreRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, storeId, date }: { recordId: string; storeId: string; date: string }) =>
      storeApi.deleteRecord(recordId, storeId, date),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STORE_KEYS.all });
    },
  });
}

export function useAssignStoreStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ storeId, userId }: { storeId: string; userId: string }) =>
      storeApi.assignStaff(storeId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: STORE_KEYS.all }),
  });
}

export function useRemoveStoreStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ storeId, userId }: { storeId: string; userId: string }) =>
      storeApi.removeStaff(storeId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: STORE_KEYS.all }),
  });
}

export function useStoreExpenses(storeId: string | null | undefined, prefix?: string) {
  return useQuery({
    queryKey: [...STORE_KEYS.all, "expenses", storeId ?? "", prefix ?? "all"] as const,
    queryFn: () => storeApi.listExpenses(storeId!, prefix),
    enabled: !!storeId,
    staleTime: 0,
  });
}

export function useStoreExpensesInfinite(storeId: string, prefix: string, category?: string) {
  return useInfiniteQuery({
    queryKey: [...STORE_KEYS.all, "expenses-infinite", storeId, prefix, category ?? "all"] as const,
    queryFn: ({ pageParam }) =>
      storeApi.listExpenses(storeId, prefix, { category, limit: RECORDS_PAGE_SIZE, lastKey: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextKey ?? undefined,
    enabled: !!storeId && !!prefix,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useCreateStoreExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStoreExpensePayload) =>
      storeApi.createExpense(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STORE_KEYS.all });
    },
  });
}

export function useUpdateStoreExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ expenseId, payload }: { expenseId: string; payload: UpdateStoreExpensePayload }) =>
      storeApi.updateExpense(expenseId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STORE_KEYS.all });
    },
  });
}

export function useDeleteStoreExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ expenseId, storeId, date }: { expenseId: string; storeId: string; date: string }) =>
      storeApi.deleteExpense(expenseId, storeId, date),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STORE_KEYS.all });
    },
  });
}

export function useStoreIncome(storeId: string | null | undefined, prefix?: string) {
  return useQuery({
    queryKey: [...STORE_KEYS.all, "income", storeId ?? "", prefix ?? "all"] as const,
    queryFn: () => storeApi.listIncome(storeId!, prefix),
    enabled: !!storeId,
    staleTime: 0,
  });
}

export function useStoreIncomeInfinite(storeId: string, prefix: string, category?: string) {
  return useInfiniteQuery({
    queryKey: [...STORE_KEYS.all, "income-infinite", storeId, prefix, category ?? "all"] as const,
    queryFn: ({ pageParam }) =>
      storeApi.listIncome(storeId, prefix, { category, limit: RECORDS_PAGE_SIZE, lastKey: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextKey ?? undefined,
    enabled: !!storeId && !!prefix,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useCreateStoreIncome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStoreIncomePayload) =>
      storeApi.createIncome(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STORE_KEYS.all });
    },
  });
}

export function useUpdateStoreIncome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incomeId, payload }: { incomeId: string; payload: UpdateStoreIncomePayload }) =>
      storeApi.updateIncome(incomeId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STORE_KEYS.all });
    },
  });
}

export function useDeleteStoreIncome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incomeId, storeId, date }: { incomeId: string; storeId: string; date: string }) =>
      storeApi.deleteIncome(incomeId, storeId, date),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STORE_KEYS.all });
    },
  });
}

export function useStoreConfig(storeId: string | null | undefined) {
  return useQuery({
    queryKey: STORE_KEYS.config(storeId ?? ""),
    queryFn: () => storeApi.getConfig(storeId!).then((r) => r.config),
    enabled: !!storeId,
    staleTime: 30_000,
  });
}

export function useSaveStoreConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveStoreConfigPayload) => storeApi.saveConfig(payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: STORE_KEYS.config(vars.storeId) });
    },
  });
}
